

import { loadSolagotchiSprites, createSolagotchiAnimations } from '../../util/Animations';
import GameService from '../../services/GameService';
import { careManager } from '../../managers/CareManager';
import { getStatsManager } from '../../managers/StatsManager';

class PlayerCharacter extends Phaser.GameObjects.Sprite {
    constructor(scene, x, y, color = 'yellow') {
        super(scene, x, y, `solagotchi_${color}`);
        
        this.color = color;
        
        this.scene = scene; // Store reference to scene
        this.lastDirection = 'right'; // Default facing right

        // Add to scene and enable physics
        scene.add.existing(this);
        scene.physics.add.existing(this);
        
        // Ensure physics body is properly set up
        if (!this.body) {
            console.warn('Physics body not created immediately, retrying...');
            // Sometimes there's a timing issue, so we try again on the next frame
            scene.time.delayedCall(1, () => {
                if (!this.body) {
                    scene.physics.add.existing(this);
                }
                if (this.body) {
                    this.setupPhysicsBody();
                }
            });
        } else {
            this.setupPhysicsBody();
        }
        
        // Movement constants
        this.SPEED = 110;
        
        // Movement lock flag
        this.movementLocked = false;
        
        // Set a fixed origin that won't be changed by the hopping animation
        this.setOrigin(0.5, 0.5);
        
        // Hopping animation properties
        this.hopOffset = 0; // Current vertical offset for hop animation
        this.hopTime = 0; // Time accumulator for hop animation
        this.hopSpeed = 0.8; // Speed of the hop animation
        this.hopHeight = 3; // Maximum height of the hop (reduced for subtlety)
        this.isMoving = false; // Track movement state
        this.lastX = x; // Track last position for smooth hopping
        this.lastY = y;
        
        // Footstep sound system
        this.footstepSounds = [];
        this.lastHopOffset = 0; // Track previous hop offset for step detection
        this.initializeFootstepSounds();
        
        // Create cursors using arrow keys AND WASD
        this.cursors = this.scene.input.keyboard.createCursorKeys();
        this.wasd = this.scene.input.keyboard.addKeys({
            up: Phaser.Input.Keyboard.KeyCodes.W,
            down: Phaser.Input.Keyboard.KeyCodes.S,
            left: Phaser.Input.Keyboard.KeyCodes.A,
            right: Phaser.Input.Keyboard.KeyCodes.D
        });
        
        // Debug flag
        this.debugMode = false;
        
        // State machine setup - will be synced with StatsManager
        this.state = {
            mood: 'idle2',  // Current animation mood
            energy: 100,    // Will sync with StatsManager
            happiness: 100, // Will be calculated by CareManager
            hunger: 100,    // Will sync with StatsManager (converted from 0-7 to 0-100)
            health: 100,    // Will sync with StatsManager  
            anger: 0
        };
        
        // State management for notifications and animations
        this.lastNotificationStates = {
            criticalHunger: false,
            criticalEnergy: false,
            starving: false,
            exhausted: false,
            richButNeglected: false
        };
        
        // Dialogue management
        this.lastDialogueTime = 0;
        this.dialogueCooldown = 5000; // 5 seconds between dialogues
        
        // Animation state management
        this.animationSwapTimer = 0;
        this.currentSwapAnimation = null;
        this.swapAnimations = [];
        
        // Care tracking
        this.careManager = careManager;
        this.lastNeglectCheck = {
            hunger: false,
            energy: false
        };
        
        // StatsManager integration
        this.statsManager = null;
        this.statsInitialized = false;
        this.retryTimer = null; // Timer for retrying StatsManager connection
        
        // Testing mode flags
        this.testingMode = {
            localOnly: false,      // If true, changes don't sync to database
            syncToDatabase: true   // If true, changes sync to database via StatsManager
        };
        
        // Initialize connection to StatsManager
        this.initializeStatsConnection();
        
        // Log initial setup
        if (this.debugMode) {
        }
    }
    
    initializeStatsConnection() {
        // Try to get StatsManager directly like other scenes do
        this.statsManager = getStatsManager();
        
        if (this.statsManager && this.statsManager.isInitialized) {
            // Load initial state from StatsManager
            this.syncFromStatsManager();
            
            // Listen for stats updates
            this.statsManager.events.on('stats-updated', (stats) => {
                this.syncFromStatsManager(stats);
            });
            
            this.statsInitialized = true;
            if (this.debugMode) {
                console.log('PlayerCharacter: Connected to StatsManager');
            }
        } else {
            console.warn('PlayerCharacter: StatsManager not available, using default state');
            // Only retry if we haven't already set up a retry timer
            if (!this.retryTimer) {
                this.retryTimer = this.scene.time.delayedCall(2000, () => {
                    this.retryTimer = null;
                    this.initializeStatsConnection();
                });
            }
        }
    }
    
    syncFromStatsManager(stats = null) {
        if (!this.statsManager) return;
        
        const currentStats = stats || this.statsManager.getStats();
        if (!currentStats) return;
        
        // Convert database stats to PlayerCharacter range (0-100)
        const hungerPercent = (currentStats.hunger / currentStats.max_hunger) * 100;
        const energyPercent = (currentStats.energy / currentStats.max_energy) * 100;
        const healthPercent = (currentStats.health / currentStats.max_health) * 100;
        
        // Update local state with database values
        this.state.hunger = Math.round(hungerPercent);
        this.state.energy = Math.round(energyPercent);
        this.state.health = Math.round(healthPercent);
        
        // Happiness is calculated by CareManager based on current stats
        this.state.happiness = this.careManager.calculateHappiness(
            this.state.hunger,
            this.state.energy
        );
        if(this.debugMode) {
           
        }
    }
    
    async syncToStatsManager() {
        if (!this.statsManager || !this.testingMode.syncToDatabase) return;
        
        try {
            // Convert PlayerCharacter percentages back to database ranges
            const currentStats = this.statsManager.getStats();
            const newHunger = Math.round((this.state.hunger / 100) * currentStats.max_hunger);
            const newEnergy = Math.round((this.state.energy / 100) * currentStats.max_energy);
            const newHealth = Math.round((this.state.health / 100) * currentStats.max_health);
            
            // Update StatsManager (which will sync to database)
            await this.statsManager.updateStats({
                hunger: newHunger,
                energy: newEnergy,
                health: newHealth
            });
            

        } catch (error) {
            console.error('PlayerCharacter: Failed to sync to database:', error);
        }
    }

    initializeFootstepSounds() {
        // Initialize footstep sounds if they exist in the cache
        for (let i = 1; i <= 5; i++) {
            const soundKey = `footsteps${i}`;
            if (this.scene.cache.audio.exists(soundKey)) {
                this.footstepSounds.push(soundKey);
            }
        }
        
    }
    
    playRandomFootstep() {
        if (this.footstepSounds.length === 0) return;
        
        // Pick a random footstep sound
        const randomIndex = Math.floor(Math.random() * this.footstepSounds.length);
        const soundKey = this.footstepSounds[randomIndex];
        
        // Play the sound with low volume so it doesn't overpower other audio
        this.scene.sound.play(soundKey, { volume: 0.15 });
    }

    setupPhysicsBody() {
        if (!this.body) return;
        
        // Set up physics body
        this.body.setCollideWorldBounds(true);
        this.body.setGravity(0);
        
        this.body.setSize(this.width * 0.6, this.height * 0.6); // Smaller collision box
        this.body.setOffset(this.width * 0.2, this.height * 0.3); // Center the collision box
        this.body.setBounce(0);
        this.body.setMaxVelocity(300, 300);
        this.body.setDragX(1000);
        this.body.setDragY(1000);
    }

    // Play animation with color prefix
    playColorAnimation(animationName, ignoreIfPlaying = true) {
        const colorAnimationKey = `${this.color}_${animationName}`;
        this.play(colorAnimationKey, ignoreIfPlaying);
    }

    // Static method to preload all player sprite variants
    static preloadAssets(scene) {
        loadSolagotchiSprites(scene);
        // console.log('PlayerCharacter: All color variants loaded');
    }
    
    // Static method to create animations - called by scenes after loading is complete
    static createAnimations(scene) {
        createSolagotchiAnimations(scene);
    }
    
    // Static method to get user's preferred color from database
    static async getUserColor(scene) {
        try {
            const gameService = new GameService(process.env.REACT_APP_API_URL || '');
            const gameStore = scene.registry.get('gameStore');
            
            if (gameStore && gameStore.walletAddress) {
                gameService.setWalletAddress(gameStore.walletAddress);
                const userOptions = await gameService.getUserOptions();
                // Extract color from player_skin path
                const skinPath = userOptions.player_skin;
                const colorMatch = skinPath.match(/solagotchi_(\w+)\.png/);
                const color = colorMatch ? colorMatch[1] : 'yellow';
                // console.log('PlayerCharacter: User color is', color);
                return color;
            } else {
                return 'yellow';
            }
        } catch (error) {
            console.error('PlayerCharacter: Error loading user color, using default:', error);
            return 'yellow';
        }
    }
    
    // New method to handle movement locking
    setMovementLocked(locked) {
        this.movementLocked = locked;
        if (locked) {
            // Stop any current movement
            this.body.setVelocity(0, 0);
            // Reset hop animation when movement is locked
            this.resetHopAnimation();
        }
    }
    
    resetHopAnimation() {
        this.hopOffset = 0;
        this.hopTime = 0;
        this.isMoving = false;
        this.lastHopOffset = 0;
        // Reset sprite display origin to default (centered) instead of regular origin
        this.setDisplayOrigin(this.width * 0.5, this.height * 0.5);
    }

    handleInput() {
        if (this.movementLocked) {
            if (this.debugMode) console.log('Movement locked - ignoring input');
            return { velocityX: 0, velocityY: 0 };
        }
        
        // Safety check: ensure physics body exists before using it
        if (!this.body) {
            if (this.debugMode) console.log('Physics body not ready');
            return { velocityX: 0, velocityY: 0 };
        }
        
        this.body.setVelocity(0);
        
        let velocityX = 0;
        let velocityY = 0;
        
        // Check for movement input (both arrow keys and WASD)
        if (this.cursors.left.isDown || this.wasd.left.isDown) {
            velocityX = -this.SPEED;
            this.lastDirection = 'left';
        } else if (this.cursors.right.isDown || this.wasd.right.isDown) {
            velocityX = this.SPEED;
            this.lastDirection = 'right';
        }
        
        if (this.cursors.up.isDown || this.wasd.up.isDown) {
            velocityY = -this.SPEED;
        } else if (this.cursors.down.isDown || this.wasd.down.isDown) {
            velocityY = this.SPEED;
        }
        
        // Apply velocity
        this.body.setVelocity(velocityX, velocityY);
        
        // Update sprite direction
        if (velocityX < 0) {
            this.setFlipX(true);
        } else if (velocityX > 0) {
            this.setFlipX(false);
        }
        
        return { velocityX, velocityY };
    }

    getLastDirection() {
        return this.lastDirection;
    }

    updateState() {
        // Handle input and movement
        const movement = this.handleInput();
        const isMoving = movement.velocityX !== 0 || movement.velocityY !== 0;
        
        // Check for critical states and trigger dialogues
        this.checkCriticalStates();
        
        // Determine primary animation based on state
        this.determineAnimation(isMoving);
        
        // Handle animation swapping for multiple conditions
        this.handleAnimationSwapping();
        
        // Return movement info for hop animation
        return movement;
    }
    
    checkCriticalStates() {
        const currentStates = {
            criticalHunger: this.state.hunger <= 10,
            criticalEnergy: this.state.energy <= 10,
            starving: this.state.hunger === 0,
            exhausted: this.state.energy === 0
        };
        
        // Track neglect events for care manager
        if (currentStates.starving && !this.lastNeglectCheck.hunger) {
            this.careManager.recordNeglect('hunger');
            this.lastNeglectCheck.hunger = true;
        } else if (!currentStates.starving) {
            this.lastNeglectCheck.hunger = false;
        }
        
        if (currentStates.exhausted && !this.lastNeglectCheck.energy) {
            this.careManager.recordNeglect('energy');
            this.lastNeglectCheck.energy = true;
        } else if (!currentStates.exhausted) {
            this.lastNeglectCheck.energy = false;
        }
        
        // Calculate happiness based on care quality and current stats
        this.state.happiness = this.careManager.calculateHappiness(
            this.state.hunger,
            this.state.energy
        );
        
        // Check for dialogue triggers with priority system and cooldown
        const currentTime = this.scene.time.now;
        const timeSinceLastDialogue = currentTime - this.lastDialogueTime;
        
        // Only trigger dialogues if cooldown has passed
        if (timeSinceLastDialogue >= this.dialogueCooldown) {
            // Define priority order (highest to lowest)
            const statesPriority = [
                'starving',
                'exhausted', 
                'criticalHunger',
                'criticalEnergy'
            ];
            
            // Find the highest priority state that has changed from false to true
            let mostCriticalState = null;
            for (const stateKey of statesPriority) {
                if (currentStates[stateKey] && !this.lastNotificationStates[stateKey]) {
                    mostCriticalState = stateKey;
                    break; // Stop at first (highest priority) match
                }
            }
            
            // Trigger dialogue only for the most critical state
            if (mostCriticalState) {
                this.triggerStateDialogue(mostCriticalState);
                this.lastDialogueTime = currentTime;
            }
        }
        
        // Update last notification states
        this.lastNotificationStates = currentStates;
    }
    
    triggerStateDialogue(stateKey) {
        // Import DialogueWindow dynamically to avoid circular dependencies
        import('../ui/DialoguePanel.js').then(({ DialogueWindow }) => {
            // Use camera-relative positioning to ensure dialogue is always visible
            const dialogue = DialogueWindow.createCameraRelative(this.scene);
            
            // Mark this as a mood dialogue that can be dismissed by movement
            dialogue.isMoodDialogue = true;
            
            // Store reference in scene for movement dismissal
            if (this.scene.activeMoodDialogue) {
                this.scene.activeMoodDialogue.destroy();
            }
            this.scene.activeMoodDialogue = dialogue;
            
            switch(stateKey) {
                case 'starving':
                    dialogue.setMultiDialogue([
                        ["I'm starving! I need food RIGHT NOW!", "angry"],
                        ["Please feed me or I might get sick...", "sad"]
                    ]);
                    break;
                case 'exhausted':
                    dialogue.setMultiDialogue([
                        ["I'm completely exhausted...", "asleep"],
                        ["I can barely keep my eyes open...", "sad"]
                    ]);
                    break;
                case 'criticalHunger':
                    dialogue.setMultiDialogue([
                        ["I'M HUNGRY YOU FUCKING FREAK! FEED ME!!!", "exclamation"],
                        ["DO YOU WANT TO SEE ME DIE?!?!", "question"],
                        ["This is... what I'll... look... like... *ughhh*", "dead"],
                    ]);
                    break;
                case 'criticalEnergy':
                    dialogue.setDialogue("I'm feeling very tired... I need rest!", "asleep");
                    break;
            }
        }).catch(error => {
            console.error('Error loading DialogueWindow:', error);
        });
    }
    
    determineAnimation(isMoving) {
        // Clear previous swap animations
        this.swapAnimations = [];
        
        // Critical states override everything
        if (this.state.hunger === 0 && this.state.energy === 0) {
            // Both starving and exhausted - swap between angry and tired
            this.swapAnimations = ['angry2', 'tired3'];
            return;
        }
        
        if (this.state.hunger === 0) {
            // Starving - very angry
            this.state.mood = 'angry2';
            return;
        }
        
        if (this.state.energy === 0) {
            // Exhausted - very tired
            this.state.mood = 'tired3';
            return;
        }
        
        // Multiple low conditions - use swapping
        const lowConditions = [];
        if (this.state.hunger <= 20) lowConditions.push('hungry');
        if (this.state.energy <= 20) lowConditions.push('tired');
        
        if (lowConditions.length > 1) {
            // Multiple conditions - swap animations
            this.swapAnimations = [];
            if (lowConditions.includes('hungry')) this.swapAnimations.push('angry1');
            if (lowConditions.includes('tired')) this.swapAnimations.push('tired2');
            return;
        }
        
        // Single primary condition
        if (this.state.hunger <= 20) {
            this.state.mood = 'angry1';
            return;
        }
        
        if (this.state.energy <= 30) {
            this.state.mood = isMoving ? 'tired1' : 'tired2';
            return;
        }
        
        // Normal state based on happiness and movement
        if (isMoving) {
            if (this.state.happiness > 80) {
                this.state.mood = 'happy1';
            } else if (this.state.happiness > 60) {
                this.state.mood = 'idle2';
            } else if (this.state.happiness < 30) {
                this.state.mood = 'sad1';
            } else {
                this.state.mood = 'idle2';
            }
        } else {
            // Idle animations
            if (this.state.happiness > 90) {
                this.state.mood = 'happy3';
            } else if (this.state.happiness > 70) {
                this.state.mood = 'happy2';
            } else if (this.state.happiness > 50) {
                this.state.mood = 'idle1';
            } else if (this.state.happiness < 30) {
                this.state.mood = 'sad1';
            } else {
                this.state.mood = 'idle2';
            }
        }
    }
    
    handleAnimationSwapping() {
        if (this.swapAnimations.length > 1) {
            // Handle animation swapping for multiple conditions
            this.animationSwapTimer += 16; // Assuming 60fps, increment by ~16ms
            
            if (this.animationSwapTimer >= 2000) { // Swap every 2 seconds
                this.animationSwapTimer = 0;
                
                // Find current animation index and switch to next
                const currentIndex = this.swapAnimations.indexOf(this.currentSwapAnimation);
                const nextIndex = (currentIndex + 1) % this.swapAnimations.length;
                this.currentSwapAnimation = this.swapAnimations[nextIndex];
                this.state.mood = this.currentSwapAnimation;
            } else if (!this.currentSwapAnimation) {
                // Initialize first swap animation
                this.currentSwapAnimation = this.swapAnimations[0];
                this.state.mood = this.currentSwapAnimation;
            }
        } else {
            // Reset swap system if not needed
            this.animationSwapTimer = 0;
            this.currentSwapAnimation = null;
        }
        
        // Play the determined animation
        this.playColorAnimation(this.state.mood);
    }

    updateHopAnimation(isMoving) {
        // Update movement state
        this.isMoving = isMoving;
        
        if (isMoving) {
            // Store previous hop offset for step detection
            this.lastHopOffset = this.hopOffset;
            
            // Increment hop time for smooth animation
            this.hopTime += 0.1; // Slower, more natural timing
            
            // Create a sine wave for smooth hopping motion
            this.hopOffset = Math.abs(Math.sin(this.hopTime * this.hopSpeed)) * this.hopHeight;
            
            // Detect footstep (when foot hits ground - crossing from high to low)
            // This happens when hopOffset goes from > 1 to <= 1 (near ground level)
            if (this.lastHopOffset > 1 && this.hopOffset <= 1) {
                this.playRandomFootstep();
            }
        } else {
            // When not moving, gradually settle to ground
            if (this.hopOffset > 0) {
                this.hopOffset *= 0.85; // Smooth decay
                if (this.hopOffset < 0.1) {
                    this.hopOffset = 0;
                    this.hopTime = 0; // Reset hop time when settled
                    this.lastHopOffset = 0; // Reset tracking
                }
            }
        }
        
        // Apply visual hop offset using display position instead of origin
        // This preserves collision detection by keeping the physics body position stable
        this.setDisplayOrigin(this.width * 0.5, (this.height * 0.5) + this.hopOffset);
    }

    update() {
        if (this.movementLocked) return;
        
        // Safety check: ensure physics body exists before updating
        if (!this.body) {
            if (this.debugMode) console.log('Physics body not ready for update');
            return;
        }
        
        // Update state and get movement info
        const movement = this.updateState();
        
        // Update hop animation based on movement
        const isMoving = movement.velocityX !== 0 || movement.velocityY !== 0;
        this.updateHopAnimation(isMoving);
        
        // Debug output
        if (this.debugMode) {
        }
    }

    modifyHappiness(amount) {
        this.state.happiness = Math.max(0, Math.min(100, this.state.happiness + amount));
        // Happiness is calculated by CareManager, not stored in database
    }

    modifyAnger(amount) {
        this.state.anger = Math.max(0, Math.min(100, this.state.anger + amount));
        // Anger is local only, not stored in database
    }

    async modifyEnergy(amount, syncToDb = true) {
        const oldEnergy = this.state.energy;
        this.state.energy = Math.max(0, Math.min(100, this.state.energy + amount));
        if (this.debugMode) {
            console.log('Energy modified: ', oldEnergy, '→', this.state.energy, '(', amount > 0 ? '+' : '', amount, ')');
        }
        // Sync to database if enabled and connected
        if (syncToDb && this.testingMode.syncToDatabase) {
            await this.syncToStatsManager();
        }
    }
    
    async modifyHunger(amount, syncToDb = true) {
        const oldHunger = this.state.hunger;
        this.state.hunger = Math.max(0, Math.min(100, this.state.hunger + amount));
        if (this.debugMode) {
            console.log('Hunger modified: ', oldHunger, '→', this.state.hunger, '(', amount > 0 ? '+' : '', amount, ')');
        }
        // Track feeding events (when hunger increases)
        if (amount > 0 && this.state.hunger > oldHunger) {
            this.careManager.recordFeeding();
        }
        
        // Sync to database if enabled and connected
        if (syncToDb && this.testingMode.syncToDatabase) {
            await this.syncToStatsManager();
        }
    }

    async modifyHealth(amount, syncToDb = true) {
        const oldHealth = this.state.health;
        this.state.health = Math.max(0, Math.min(100, this.state.health + amount));
        if (this.debugMode) {
            console.log('Health modified: ', oldHealth, '→', this.state.health, '(', amount > 0 ? '+' : '', amount, ')');
        }
        // Sync to database if enabled and connected
        if (syncToDb && this.testingMode.syncToDatabase) {
            await this.syncToStatsManager();
        }
    }
    
    // Utility methods for getting current state
    getState() {
        return { ...this.state };
    }
    
    // Method to set multiple state values at once
    async setState(newState, syncToDb = true) {
        const oldState = { ...this.state };
        
        Object.keys(newState).forEach(key => {
            if (this.state.hasOwnProperty(key)) {
                this.state[key] = Math.max(0, Math.min(100, newState[key]));
            }
        });

        // Sync to database if enabled and connected
        if (syncToDb && this.testingMode.syncToDatabase) {
            await this.syncToStatsManager();
        }
    }
    
    // Testing mode controls
    setTestingMode(mode) {
        this.testingMode = { ...this.testingMode, ...mode };
    }
    
    // Force sync current state to database (for testing)
    async forceSyncToDatabase() {
        await this.syncToStatsManager();
    }
    
    // Force reload state from database (for testing)
    async forceReloadFromDatabase() {
        this.syncFromStatsManager();
    }
    
    // For testing - gradually decrease stats over time
    enableStatDecay(decayRate = 0.1) {
        if (this.statDecayTimer) {
            this.statDecayTimer.remove();
        }
        
        this.statDecayTimer = this.scene.time.addEvent({
            delay: 5000, // Every 5 seconds
            callback: () => {
                this.modifyHunger(-decayRate);
                this.modifyEnergy(-decayRate * 0.5); // Energy decays slower
            },
            loop: true
        });
    }
    
    disableStatDecay() {
        if (this.statDecayTimer) {
            this.statDecayTimer.remove();
            this.statDecayTimer = null;
        }
    }
    
    // Cleanup method to be called before destroying the character
    preDestroy() {
        // Clean up retry timer
        if (this.retryTimer) {
            this.retryTimer.remove();
            this.retryTimer = null;
        }
        
        // Clean up stat decay timer
        this.disableStatDecay();
        
        // Remove event listeners
        if (this.statsManager && this.statsManager.events) {
            this.statsManager.events.off('stats-updated');
        }
    }
    
    // Debug helper to log current state with animation info
    logStateInfo() {
       
        
        if (this.statsManager) {
            const dbStats = this.statsManager.getStats();
           
        }
      
    }
}

export default PlayerCharacter;